import React, { memo, useState, useEffect } from 'react';
import { Mail, X, Download, FileText, AlertCircle, ClipboardCheck, Calendar } from 'lucide-react';
import { formatDate, buildMyCaseTaskUrl, buildMyCaseEventUrl } from '../utils/helpers';
import { getIMSMappingByEmail } from '../models/IMSModel';
import AttachmentIcons from './AttachmentIcons';
import './AttachmentPreview.css';

// Reuse same tag color hashing used in EmailView for consistent order-type colors
const TAG_COLORS = [
  'tag-blue',
  'tag-purple',
  'tag-green',
  'tag-red',
  'tag-yellow',
  'tag-teal',
  'tag-pink',
  'tag-indigo'
];

const getOrderTypeClass = (type = '') => {
  let hash = 0;
  for (let i = 0; i < type.length; i++) {
    hash = type.charCodeAt(i) + ((hash << 5) - hash);
  }
  const colorIndex = Math.abs(hash) % TAG_COLORS.length;
  return `tag ${TAG_COLORS[colorIndex]}`;
};

// Canonical list used by the UI — keep in sync with EmailView
const CANONICAL_ORDER_TYPES = [
  'Order Granting Continuance',
  'Filing Deadline Orders',
  'Order Granting Motion to Change Venue',
  'Administrative Closure',
  'Telephonic/VTC Orders',
  'EOIR Notification',
  'Order Sustaining Charges',
  'Final Removal Order',
  'Written Decisions',
  'Continuance Denied',
  'Order on Motion to Reconsider',
  'Order Granting Motion to Reopen',
  'Order of Voluntary Departure Grant',
  'Termination/Dismissal Denied',
  'Order Granting Continuance, EOIR Notification',
  'Order Granting Continuance, Telephonic/VTC Orders',
  'Order Sustaining Charges, Filing Deadline Orders',
  'Order Granting Motion to Change Venue, Filing Deadline Orders',
  'Final Removal Order, Filing Deadline Orders',
  'Other'
];

const EmailDetail = memo(({ email, onClose }) => {
    const [pdfUrl, setPdfUrl] = useState(null);
    const [pdfError, setPdfError] = useState(false);
    const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1.2);
  const [imsTasks, setImsTasks] = useState([]); // array of task objects
  const [imsEvents, setImsEvents] = useState([]); // array of event objects
  const [imsError, setImsError] = useState(null);

    useEffect(() => {
        // Check if we have a PDF attachment
        if (email.attachment_base64) {
            try {
                // If base64 encoded PDF
                const base64Data = email.attachment_base64.includes('base64,') 
                    ? email.attachment_base64 
                    : `data:application/pdf;base64,${email.attachment_base64}`;
                setPdfUrl(base64Data);
            } catch (error) {
                console.error('Error processing PDF:', error);
                setPdfError(true);
            }
        } else if (email.attachment_url) {
            // If URL to PDF
            setPdfUrl(email.attachment_url);
        }
        // Don't try to process attachment_content as PDF - it's just text

        return () => {
            // Cleanup blob URLs
            if (pdfUrl && pdfUrl.startsWith('blob:')) {
                URL.revokeObjectURL(pdfUrl);
            }
        };
    }, [email]);

  // Fetch IMS mappings by email_id (task/event IDs)
  useEffect(() => {
    let aborted = false;
    async function loadIMS() {
      setImsError(null);
      setImsTasks([]);
      setImsEvents([]);
      try {
        const id = email?.email_id || email?.id || email?.message_id;
        if (!id) return;
        const res = await getIMSMappingByEmail(id);
        if (aborted || !res) return;
  // model now returns arrays of task/event objects
  setImsTasks(res.tasks || []);
  setImsEvents(res.events || []);
      } catch (e) {
        if (!aborted) setImsError(e?.message || 'Failed to load IMS mapping');
      }
    }
    loadIMS();
    return () => { aborted = true; };
  }, [email]);

    const handleDownload = (format = 'txt') => {
        const clientName = email.client_name || 'Unknown_Client';
        const sanitizedClientName = clientName.replace(/[^a-zA-Z0-9\-_]/g, '_');
        
        if (email.attachment_content) {
            try {
                let filename, blob;
                
                // Create a formatted text document with proper headers
                const formattedContent = `LEGAL DOCUMENT
${new Date().toISOString().split('T')[0]}

Subject: ${email.subject || 'Immigration Document'}
Client: ${email.client_name || 'Unknown Client'}
A Number: ${email.a_number || 'N/A'}

${'-'.repeat(80)}

${email.attachment_content}

${'-'.repeat(80)}

Document generated from email: ${email.email_id || ''}
Classification timestamp: ${email.classification_timestamp || new Date().toISOString()}
`;
                
                if (format === 'pdf') {
                    // For PDF, we'll save as text but with PDF extension
                    filename = email.attachment_filename || `${sanitizedClientName}_document.pdf`;
                    blob = new Blob([formattedContent], { 
                        type: 'application/octet-stream'
                    });
                } else {
                    // For TXT format
                    filename = email.attachment_filename 
                        ? email.attachment_filename.replace('.pdf', '.txt')
                        : `${sanitizedClientName}_document.txt`;
                    blob = new Blob([formattedContent], { 
                        type: 'text/plain;charset=utf-8'
                    });
                }
                
                // Create download link
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.style.display = 'none';
                a.href = url;
                a.download = filename;
                
                // Trigger download
                document.body.appendChild(a);
                a.click();
                
                // Cleanup
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
                
                console.log('Download initiated:', filename);
                
            } catch (error) {
                console.error('Download error:', error);
                alert('Download failed. Please try again.');
            }
        } else {
            alert('No attachment content available for download.');
        }
    };

    const hasPdfAttachment = pdfUrl && !pdfError;

    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" style={{ maxWidth: '95vw', maxHeight: '95vh' }} onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <div className="modal-header-content">
              <div className="modal-header-left">
                <div className="modal-icon-wrapper">
                  <Mail size={24} color="var(--accent-color)" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h3 className="modal-title">Email Details</h3>
                  <p className="modal-subtitle">
                    {email.email_id}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <button className="modal-close" onClick={onClose}>
                  <X size={20} />
                </button>
              </div>
            </div>
          </div>
          <div className="modal-body" style={{ maxHeight: '80vh' }}>
            <div className="modal-field">
              <label className="form-label">Subject</label>
              <p className="modal-text">{email.subject}</p>
            </div>
            
            <div className="modal-field">
              <label className="form-label">Email Body</label>
              <div 
                className="modal-text-box" 
                style={{ 
                  maxHeight: '12rem',
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word',
                  wordBreak: 'break-word',
                  lineHeight: '1.6',
                  fontSize: '0.9rem',
                  padding: '1rem',
                  backgroundColor: 'var(--muted-light-bg)',
                  border: '1px solid var(--border-light)',
                  borderRadius: '0.5rem',
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                  color: 'var(--text-dark)'
                }}
              >
                {email.body}
              </div>
            </div>
            
            <div className="modal-field-grid">
              <div>
                <label className="form-label">Client Name</label>
                <p className="modal-text-small">{email.client_name || "Unknown"}</p>
              </div>
              
              <div>
                <label className="form-label">A Number</label>
                <p className="modal-text-code">{email.a_number || "N/A"}</p>
              </div>
              
              <div>
                <label className="form-label">Priority</label>
                <p style={{
                  fontWeight: '600',
                  fontSize: '1.125rem',
                  color: email.prediction_priority_value === "High" 
                    ? "var(--danger)" 
                    : email.prediction_priority_value === "Normal" 
                    ? "var(--success)" 
                    : "var(--neutral)"
                }}>
                  {email.prediction_priority_value}
                </p>
              </div>
              
              <div>
                <label className="form-label">Email Received</label>
                <p className="modal-text-code" style={{ fontSize: '1rem' }}>
                  {email.classification_timestamp 
                    ? formatDate(email.classification_timestamp, "YYYY-MM-DD HH:mm")
                    : "-"
                  }
                </p>
              </div>
              <div>
                <label className="form-label">MyCase TASK</label>
                <div className="modal-text" style={{ fontSize: '0.95rem' }}>
                    {imsTasks.length > 0 ? (
                      <div style={{ display: 'flex', gap: '0.75rem', flexDirection: 'column' }}>
                        {imsTasks.map((task) => (
                          <a
                            key={task.id}
                            href={buildMyCaseTaskUrl(task.id)}
                            target="_blank"
                            rel="noreferrer"
                            title={task.task_name || `Open IMS Task ${task.id}`}
                            aria-label={task.task_name || `Open IMS Task ${task.id}`}
                            style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: '0.75rem',
                              padding: '0.5rem',
                              backgroundColor: 'var(--card-bg-start)',
                              border: '1px solid var(--border-light)',
                              borderRadius: '0.5rem',
                              color: 'var(--fg-color)',
                              textDecoration: 'none',
                            }}
                          >
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
                                <div style={{ fontWeight: 600 }}>{task.task_name || `Task ${task.id}`}</div>
                                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                    {task.priority ? (
                                    <span style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', borderRadius: '999px', backgroundColor: 'var(--muted-light-bg)', color: 'var(--text-dark)' }}>{task.priority}</span>
                                  ) : null}
                                  {task.due_date ? (
                                    <span style={{ fontSize: '0.75rem', color: 'var(--neutral)' }}>{formatDate(task.due_date, 'YYYY-MM-DD')}</span>
                                  ) : null}
                                </div>
                              </div>
                                {task.description ? (
                                <div style={{ marginTop: '0.5rem', color: 'var(--muted)', fontSize: '0.9rem' }}>{task.description}</div>
                              ) : null}
                            </div>
                          </a>
                        ))}
                      </div>
                    ) : (
               <span style={{ color: 'var(--neutral)' }}>None</span>
                    )}
                  </div>
              </div>
              <div>
                <label className="form-label">Order Types</label>
                <div className="modal-text" style={{ fontSize: '0.95rem' }}>
                  {Array.isArray(email.order_type) && email.order_type.length > 0 ? (
                    (() => {
                      // Build a set of canonical tags present and aggregate others
                      const present = new Map();
                      let otherCount = 0;
                      email.order_type.forEach((ot) => {
                        if (CANONICAL_ORDER_TYPES.includes(ot)) {
                          present.set(ot, (present.get(ot) || 0) + 1);
                        } else {
                          otherCount += 1;
                        }
                      });

                      const nodes = [];
                      CANONICAL_ORDER_TYPES.forEach((type) => {
                        if (type === 'Other') {
                          if (otherCount > 0) {
                            nodes.push(<span key="other" className={getOrderTypeClass('Other')}>Other</span>);
                          }
                        } else if (present.has(type)) {
                          nodes.push(<span key={type} className={getOrderTypeClass(type)}>{type}</span>);
                        }
                      });

                      return <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>{nodes}</div>;
                    })()
                  ) : (
                    <span style={{ color: 'var(--neutral)' }}>{(Array.isArray(email.order_type) ? email.order_type.join(', ') : email.prediction_order_type_value) || 'None'}</span>
                  )}
                </div>
              </div>
              <div>
                <label className="form-label">Mycase Events</label>
                <div className="modal-text" style={{ fontSize: '0.95rem' }}>
                  {imsEvents.length > 0 ? (
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      {imsEvents.map((event) => (
                        <a
                          key={event.id}
                          href={buildMyCaseEventUrl(event.id)}
                          target="_blank"
                          rel="noreferrer"
                          title={event.title || `Open IMS Event ${event.id}`}
                          aria-label={event.title || `Open IMS Event ${event.id}`}
                          style={{
                            display: 'block',
                            padding: '0.5rem',
                            backgroundColor: 'var(--card-bg-start)',
                            border: '1px solid var(--border-light)',
                            borderRadius: '0.5rem',
                            color: 'var(--fg-color)',
                            textDecoration: 'none',
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}>
                            <div style={{ fontWeight: 600 }}>{event.title || `Event ${event.id}`}</div>
                            {event.start_date ? (
                      <div style={{ fontSize: '0.85rem', color: 'var(--neutral)' }}>{event.start_date}</div>
                            ) : null}
                          </div>
           {event.description ? (
         <div style={{ marginTop: '0.5rem', color: 'var(--muted)', fontSize: '0.9rem' }}>{event.description}</div>
           ) : null}
                        </a>
                      ))}
                    </div>
                    ) : (
                    <span style={{ color: 'var(--neutral)' }}>None</span>
                    )}
                </div>
              </div>
            </div>
  
            {/* PDF Attachment Viewer/Download */}
             {/* Attachment Details: show icons for all attachments and preview on click */}
             <div className="modal-field">
               <label className="form-label">Attachment Details</label>
               <div style={{ marginTop: 6 }}>
                 {/* messageId: backend expects the message identifier used in /attachments/messages/{id}/attachments */}
                 <AttachmentIcons messageId={email?.email_id || email?.id || email?.message_id} />
               </div>
             </div>
            
            {/* Error state */}
                  {pdfError && (
              <div className="modal-field">
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '1rem',
                  backgroundColor: 'var(--danger-light)',
                  border: '1px solid var(--danger-border)',
                  borderRadius: '0.5rem',
                  color: 'var(--danger)'
                }}>
                  <AlertCircle size={20} />
                  <span>Failed to load PDF attachment</span>
                </div>
              </div>
            )}
            
            <div>
              <label className="form-label">Classification Timestamp</label>
              <p className="modal-text-code" style={{ fontSize: '1rem' }}>
                {email.classification_timestamp 
                  ? formatDate(email.classification_timestamp)
                  : "-"
                }
              </p>
            </div>
          </div>
        </div>

        {/* Document Viewer Modal */}
        {showPdfViewer && (email.attachment_content || hasPdfAttachment) && (
          <div 
            className="modal-overlay" 
            style={{ zIndex: 9999 }}
            onClick={() => setShowPdfViewer(false)}
          >
            <div 
              className="modal-content" 
              style={{ 
                maxWidth: '98vw', 
                maxHeight: '98vh',
                width: '95vw',
                height: '95vh'
              }} 
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <div className="modal-header-content">
                    <div className="modal-header-left">
                    <div className="modal-icon-wrapper">
                      <FileText size={24} color="var(--danger)" />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h3 className="modal-title">Document Viewer</h3>
                      <p className="modal-subtitle">
                        {email.attachment_filename || `${email.client_name || 'Unknown Client'}_document.pdf`}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleDownload('pdf')}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.5rem 1rem',
                        backgroundColor: 'var(--danger)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '0.375rem',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                      }}
                    >
                      <Download size={16} />
                      PDF
                    </button>
                    <button
                      onClick={() => handleDownload('txt')}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.5rem 1rem',
                        backgroundColor: 'var(--success)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '0.375rem',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                      }}
                    >
                      <Download size={16} />
                      TXT
                    </button>
                    <button className="modal-close" onClick={() => setShowPdfViewer(false)}>
                      <X size={20} />
                    </button>
                  </div>
                </div>
              </div>
              <div className="modal-body" style={{ maxHeight: '85vh', padding: '1rem' }}>
                {/* Text Content Viewer */}
                <div 
                  style={{
                    width: '100%',
                    height: '100%',
                    minHeight: '75vh',
                    border: '1px solid var(--border-light)',
                    borderRadius: '0.5rem',
                    overflow: 'auto',
                    backgroundColor: 'var(--muted-light-bg)',
                    padding: '1.5rem',
                    whiteSpace: 'pre-wrap',
                    wordWrap: 'break-word',
                    wordBreak: 'break-word',
                    lineHeight: '1.6',
                    fontSize: '0.95rem',
                    fontFamily: 'system-ui, -apple-system, sans-serif',
                    color: 'var(--text-dark)'
                  }}
                >
                  {email.attachment_content || 'No content available to display.'}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  });

export default EmailDetail;
